import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Star, Share2, Flag, ChevronUp, ChevronDown } from 'lucide-react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsDetail from '../core/HeadersAndFooters/HeaderButtonsDetail';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsDetail from '../core/HeadersAndFooters/FooterButtonsDetail';
import StandardButton from '../core/StandardButton';
import ErrorReportModal from '../core/ErrorReportModal';
import ConfirmationModal from '../core/ConfirmationModal';
import ShareLinkModal from '../core/ShareLinkModal';
import ReportModal from '../shared/ReportModal';
import ConcertinaThread from '../chat/ConcertinaThread';
import NewsGrid from './NewsGrid';
import { chatService, userActionService } from '../../data/dataService';
import { supabaseFetch } from '../../lib/supabase';
import { useTheme } from '../../themes/ThemeProvider';
import useMediaQuery from '../../hooks/useMediaQuery';
import useCommentBarLayout from '../../hooks/useCommentBarLayout';
import { useTranslation } from '../../i18n/useTranslation';
import { formatTextWithStyles } from '../../utils/formatText';

// Collect all flat descendants of a root comment (engine's loadResponses shape)
const collectDescendants = (rootId, all) => {
  const out = [];
  for (const d of all.filter(c => c.parent_chat_id === rootId)) {
    out.push(d);
    out.push(...collectDescendants(d.id, all));
  }
  return out;
};

const ArticleDetail = ({ article, onBack, onViewGroup, context, userId, articles = [], onArticleClick }) => {
  const scrollRef = useRef(null);
  const contentAreaRef = useRef(null);
  const { isMobile, sidebarWidth, isSidebarCompact, windowWidth, headerHeight } = useMediaQuery('detail');
  const { t, formattingLocale } = useTranslation('detail');

  // In-place news (July 2026) — 1280px is this feature's product floor (distinct from the
  // 1200px mobile boundary; below 1280 the sidebar+article+comments layout is untouched).
  // Reuses NewsGrid whole (the same component NewsFeed mounts) rather than a bespoke card
  // list — its own auto-fit(250px) CSS grid decides 1-vs-2+ columns from whatever width the
  // flex:1 slot resolves to, so no separate breakpoint is computed here. Not rendered at all
  // (not rendered empty) when the caller has no article list — e.g. a guest arriving via a
  // /l/ smart link.
  const newsColumnArticles = (articles || []).filter(
    a => a.article_type === 'news' || a.article_type === 'website'
  );

  // Mobile comments panel state
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const { pageBottomPadding, scrollHeight } = useCommentBarLayout(commentsExpanded);

  // Comment system state — flat list owned here; the ConcertinaThread engine owns rendering
  const [allComments, setAllComments] = useState([]);

  // Rating state
  const [userRating, setUserRating] = useState(null); // User's current rating (1-5)
  const [currentUserId, setCurrentUserId] = useState(null);

  // View count from article_engagement table
  const [viewCount, setViewCount] = useState(article.view_count || 0);
  // Comment count from article_engagement table
  const [commentCount, setCommentCount] = useState(article.comment_count || 0);

  // Modal state
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  // Desktop article detail has no FooterContainer (addendum-1, 2026-09-05) — the Help button
  // it used to carry is rehomed as its own floating control, matching the front-page precedent
  // (MainFeed.jsx) for pages that run footerless.
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [showOtherArticles, setShowOtherArticles] = useState(false);
  const [otherArticles, setOtherArticles] = useState([]);
  const [otherArticlesLoading, setOtherArticlesLoading] = useState(false);

  // Debug: Log when showShareModal changes
  useEffect(() => {
    console.log('🔍 showShareModal state changed:', showShareModal, 'isMobile:', isMobile);
  }, [showShareModal, isMobile]);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Get theme from context
  const theme = useTheme();

  // VIDEO EMBED LOGIC - Parses URLs and returns embed iframe source
  const getVideoEmbed = (url) => {
    if (!url) return null;

    // YouTube: youtube.com/watch?v= or youtu.be/
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId;
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
      } else if (url.includes('youtube.com')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get('v');
      }
      if (videoId) {
        return `https://www.youtube-nocookie.com/embed/${videoId}`;
      }
    }

    // Vimeo: vimeo.com/[id]
    if (url.includes('vimeo.com')) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}`;
      }
    }

    // PeerTube: framatube.org/w/ or /videos/watch/
    if (url.includes('framatube.org') || url.includes('peertube')) {
      try {
        const urlObj = new URL(url);
        const origin = urlObj.origin;

        let videoPath;
        if (url.includes('/w/')) {
          videoPath = url.split('/w/')[1].split('?')[0];
        } else if (url.includes('/videos/watch/')) {
          videoPath = url.split('/videos/watch/')[1].split('?')[0];
        }

        if (videoPath) {
          return `${origin}/videos/embed/${videoPath}`;
        }
      } catch (e) {
        console.error('Error parsing PeerTube URL:', e);
      }
    }

    return null; // Fallback: unrecognized format
  };

  const videoEmbedUrl = getVideoEmbed(article?.video_url);
  const isVideoArticle = !!article?.video_url;
  const isWebsiteArticle = article?.article_type === 'website' && !!article?.website_url;

  // The article container must hug its content's own max-width (matching renderArticleBody's
  // per-type wrapper: 900/800/600 + 48px for the shared p-6/24px padding), not flex-grow to
  // fill whatever's left in the row — that flex-grow was leaving a large empty gap between the
  // end of the article text and the Comments panel whenever the row had more width than the
  // text needed.
  const articleContainerMaxWidth = isWebsiteArticle ? 948 : isVideoArticle ? 848 : 648;

  // Scroll to top handler for footer button
  useEffect(() => {
    // Listen for scroll to start event from footer button
    const handleScrollToStart = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    };

    window.addEventListener('ournet:scrollToStart', handleScrollToStart);

    return () => {
      window.removeEventListener('ournet:scrollToStart', handleScrollToStart);
    };
  }, []);

  // Load user ID, rating, and like/dislike status when article loads
  useEffect(() => {
    const loadUserAndRating = async () => {
      if (!article?.id) {
        return;
      }

      try {
        // Load view count from article_engagement table (runs for all visitors)
        const engagementViewCount = await userActionService.getArticleEngagementCount(article.id, 'view');
        if (engagementViewCount !== null) {
          setViewCount(engagementViewCount);
        }

        // Load comment count from article_engagement table
        const engagementCommentCount = await userActionService.getArticleEngagementCount(article.id, 'comment');
        if (engagementCommentCount !== null) {
          setCommentCount(engagementCommentCount);
        }

        if (userId) {
          setCurrentUserId(userId);

          // Record view for reputation (fire-and-forget, non-blocking)
          userActionService.trackView(article.id, userId).catch(() => {});

          // Load user's existing rating for this article
          const rating = await userActionService.getArticleRating(userId, article.id);
          setUserRating(rating);
        }
      } catch (error) {
        console.error('Error loading user rating:', error);
      }
    };

    loadUserAndRating();
  }, [article, userId]);

  // --- Comment data wiring (consumer owns data + count; engine owns render/interaction) ---

  // Re-fetch all comments as a FLAT list and update count.
  const reloadComments = useCallback(async () => {
    if (!article?.id) return;
    try {
      const data = await chatService.getArticleComments(article.id);
      setAllComments(data || []);
      setCommentCount((data || []).filter(c => !c.is_deleted_by_author).length);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  }, [article?.id]);

  // Load comments from database when article loads
  useEffect(() => {
    reloadComments();
  }, [reloadComments]);

  // Top-level comments, each annotated with a live reply_count for the engine.
  const topLevelComments = useMemo(
    () => allComments
      .filter(c => !c.parent_chat_id)
      .map(c => ({
        ...c,
        reply_count: collectDescendants(c.id, allComments).filter(r => !r.is_deleted_by_author).length
      })),
    [allComments]
  );

  // Flat descendants of a root comment, on demand.
  const loadResponses = useCallback(
    (rootId) => Promise.resolve(collectDescendants(rootId, allComments)),
    [allComments]
  );

  // Create a top-level comment (engine calls onRefresh itself after).
  const onCreateTopLevel = useCallback(async ({ content }) => {
    await chatService.createArticleComment({
      content,
      article_id: article.id,
      parent_chat_id: null,
      author_id: currentUserId
    });
    userActionService.trackComment(article.id, currentUserId).catch(() => {});
  }, [article.id, currentUserId]);

  // Create a reply (engine calls onRefresh itself after).
  const onCreateReply = useCallback(async ({ parentChatId, content }) => {
    await chatService.createArticleComment({
      content,
      article_id: article.id,
      parent_chat_id: parentChatId,
      author_id: currentUserId
    });
  }, [article.id, currentUserId]);

  // Handle star rating click
  const handleStarClick = async (rating) => {
    console.log('Star clicked:', rating);
    console.log('Current user ID:', currentUserId);
    console.log('Article ID:', article?.id);

    if (!currentUserId) {
      console.warn('User must be logged in to rate articles');
      setModal({
        visible: true,
        message: t('auth:login_to_rate'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    try {
      console.log('Attempting to save rating...');
      const success = await userActionService.setArticleRating(currentUserId, article.id, rating);
      console.log('Save rating result:', success);

      if (success) {
        setUserRating(rating);
        console.log('Rating updated to:', rating);
      } else {
        console.error('Failed to save rating');
        setModal({
          visible: true,
          message: t('failed_to_save_rating'),
          onOk: () => {
            setModal({ visible: false, message: '', onOk: null });
          }
        });
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      setModal({
        visible: true,
        message: 'Error saving rating: ' + error.message,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    }
  };

  const handleSeeOtherArticles = async () => {
    setShowOtherArticles(true);

    if (!article.author_id) {      // removed/anonymised account — show empty state
      setOtherArticles([]);
      return;
    }

    setOtherArticlesLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('articles', {
        select: 'id,title,article_type,hero_image_thumbnail_url,cover_image_url,published_at',
        eq: { author_id: article.author_id, is_published: true, is_public: true },
        order: 'published_at.desc',
        limit: 20
      });

      if (error) {
        console.error('Error loading author articles:', error);
        setOtherArticles([]);
      } else {
        setOtherArticles((data || []).filter(a => a.id !== article.id));  // drop current article
      }
    } catch (err) {
      console.error('Exception loading author articles:', err);
      setOtherArticles([]);
    } finally {
      setOtherArticlesLoading(false);
    }
  };

  const openOtherArticle = (articleId) => {
    setShowOtherArticles(false);
    window.location.hash = `#/article/${articleId}`;
  };

  // Format content with basic paragraph support and inline images (NEW - Image System)
  const formatContent = (content) => {
    if (!content) return content;

    // Check if article has inline images
    const inlineImages = article?.inline_images || [];

    // Image size options: S = 33%, M = 66%, L = 100%
    const IMAGE_SIZES = {
      S: '33%',
      M: '66%',
      L: '100%'
    };

    // Check if content has image markers (support both [IMAGE:] legacy and [IMG:] new format)
    const hasImageMarkers = /\[(IMAGE|IMG):[^\]]+\]/.test(content);

    if (!hasImageMarkers) {
      // No image markers - use formatting with bold/italic/heading support
      return formatTextWithStyles(content, 'fc-', {
        color: '#374151',
        lineHeight: '1.625',
        marginBottom: '16px'
      });
    }

    // Has image markers - parse and render (support both [IMAGE:] and [IMG:] formats)
    // Split by image markers: [IMAGE:img-123456789] or [IMG:img-123456789]
    const parts = content.split(/\[(?:IMAGE|IMG):([^\]]+)\]/);

    return parts.map((part, index) => {
      // Even indices are text content
      if (index % 2 === 0) {
        // Text content - render with formatting support
        if (!part.trim()) return null;
        return formatTextWithStyles(part, `fc-${index}-`, {
          color: '#374151',
          lineHeight: '1.625',
          marginBottom: '16px'
        });
      } else {
        // Odd indices are image IDs
        const imageId = part;
        const image = inlineImages.find(img => img.id === imageId);

        if (image) {
          // Mobile: cap at 66% width for body images; desktop: use stored size
          const imageWidth = isMobile ? '66%' : (IMAGE_SIZES[image.size] || '100%');
          return (
            <div key={`img-${index}`} className="my-6" style={isMobile ? { textAlign: 'center' } : undefined}>
              <img
                src={image.displayUrl}
                alt={image.caption || ''}
                loading="lazy"
                style={{
                  width: imageWidth,
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  ...(isMobile ? { display: 'block', margin: '0 auto' } : {})
                }}
              />
              {(image.caption || 'credit' in image) && (
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px', maxWidth: imageWidth, textAlign: 'center', ...(isMobile ? { margin: '4px auto 0' } : {}) }}>
                  {image.caption && <div style={{ fontStyle: 'italic' }}>{image.caption}</div>}
                  {image.credit ? (
                    <div>{image.credit}</div>
                  ) : 'credit' in image ? (
                    <div>{t('image:credit.uncredited')}</div>
                  ) : null}
                </div>
              )}
            </div>
          );
        }
        // Image data not found - show placeholder or nothing
        return (
          <div key={`img-${index}`} className="my-6 p-4 bg-gray-100 rounded-lg text-center text-gray-500 text-sm">
            {t('image_not_available')}
          </div>
        );
      }
    });
  };

  if (!article) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#F8FAFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#64748B' }}>
          {t('loading_article')}
        </div>
      </div>
    );
  }

  // "See their other articles" pop-up — shared by both mobile and desktop return trees
  const otherArticlesModal = showOtherArticles ? (
    <div
      onClick={() => setShowOtherArticles(false)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
            {t('other_articles_by', { name: article.author_name || t('unknown_author') })}
          </div>
          <button
            onClick={() => setShowOtherArticles(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B7280',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            {t('common:close')}
          </button>
        </div>

        <div style={{ padding: '12px 20px', overflowY: 'auto' }}>
          {otherArticlesLoading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
              {t('common:loading')}
            </div>
          ) : otherArticles.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
              {t('no_other_articles')}
            </div>
          ) : (
            otherArticles.map((otherArticle) => (
              <button
                key={otherArticle.id}
                onClick={() => openOtherArticle(otherArticle.id)}
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  width: '100%',
                  padding: '10px 0',
                  borderBottom: '1px solid #F3F4F6',
                  background: 'none',
                  border: 'none',
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: '#F3F4F6',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <img
                  src={otherArticle.hero_image_thumbnail_url || otherArticle.cover_image_url}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '56px',
                    height: '56px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    flexShrink: 0,
                    backgroundColor: '#F3F4F6'
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#1F2937',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {otherArticle.title}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                    {otherArticle.published_at ? new Date(otherArticle.published_at).toLocaleDateString(formattingLocale) : ''}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  ) : null;

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        {/* Header - Layered architecture */}
        <HeaderContainer />
        <HeaderButtonsDetail
          currentContext={{ name: article.community_id ? article.group_name : (article.locality_name || t('local_community')) }}
          onBack={onBack}
        />

        <div style={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: '#F8FAFC',
          paddingTop: `${headerHeight}px`,
          paddingBottom: `${pageBottomPadding}px`
        }}>
        {/* Main Content - Scrollable */}
        <div style={{
          overflowY: 'auto',
          height: scrollHeight
        }}>
          {/* Hero Image - 4:3 (hidden for video articles, they show the embed below) */}
          {!isVideoArticle && (article.hero_image_display_url || article.cover_image_url) && (
            <div style={{ marginBottom: '12px', textAlign: 'center' }}>
              <div style={{
                width: '66%',
                aspectRatio: '4 / 3',
                position: 'relative',
                overflow: 'hidden',
                display: 'inline-block'
              }}>
                <img
                  src={article.hero_image_display_url || article.cover_image_url}
                  alt={article.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              {(article.image_caption || article.hero_image_credit) && (
                <div style={{
                  fontSize: '11px',
                  color: '#6B7280',
                  marginTop: '4px',
                  textAlign: 'center'
                }}>
                  {article.image_caption && (
                    <div style={{ fontStyle: 'italic' }}>{article.image_caption}</div>
                  )}
                  {article.hero_image_credit ? (
                    <div>{article.hero_image_credit}</div>
                  ) : article.hero_image_display_url ? (
                    <div>{t('image:credit.uncredited')}</div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Video for video articles */}
          {isVideoArticle && videoEmbedUrl && (
            <div style={{
              width: '100%',
              aspectRatio: '16 / 9',
              position: 'relative'
            }}>
              <iframe
                src={videoEmbedUrl}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title="Article Video"
              />
            </div>
          )}

          {/* Content Section */}
          <div style={{ padding: '16px' }}>
            {/* Title */}
            <h1 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1F2937',
              marginBottom: '12px',
              lineHeight: '1.3'
            }}>
              {article.title}
            </h1>

            {/* Summary/Introduction - Bold */}
            {article.summary && (
              <p style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                lineHeight: '1.5',
                marginBottom: '16px'
              }}>
                {article.summary}
              </p>
            )}

            {/* Article Content - Vertical scroll */}
            <div style={{
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              {formatContent(article.content)}
            </div>

            {/* End marker */}
            <div style={{
              fontSize: '13px',
              color: '#9CA3AF',
              fontStyle: 'italic',
              marginTop: '20px',
              marginBottom: '24px'
            }}>
              End
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid #E5E7EB', margin: '16px 0' }} />

            {/* Metadata Section */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {/* Group/Locality Thumbnail */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#E5E7EB'
              }}>
                {article.group_logo_url ? (
                  <img
                    src={article.group_logo_url}
                    alt={article.community_id ? (article.group_name || 'Group') : (article.locality_name || 'Locality')}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    {article.community_id ? '👥' : '📍'}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '10px',
                  color: '#6B7280',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '2px'
                }}>
                  {t('article_author')}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.primary,
                  marginBottom: '4px'
                }}>
                  {article.author_name || t('unknown_author')}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <span>{t('views')}: {viewCount}</span>
                  <span>{t('comments')}: {commentCount}</span>
                </div>
              </div>
            </div>

            {/* Rating Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '8px'
              }}>
                {t('rate_this_article')}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map((starNumber) => {
                  const isFilled = userRating !== null && userRating >= starNumber;
                  return (
                    <Star
                      key={starNumber}
                      size={28}
                      fill={isFilled ? theme.primary : 'none'}
                      stroke={theme.primary}
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleStarClick(starNumber)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <button
                onClick={() => {
                  console.log('Mobile Share button clicked');
                  setShowShareModal(true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  console.log('Mobile Share button touched');
                  setShowShareModal(true);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: theme.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                <Share2 size={16} />
                {t('share')}
              </button>

              <button
                onClick={handleSeeOtherArticles}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSeeOtherArticles();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: theme.primary,
                  border: `2px solid ${theme.primary}`,
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                {t('see_their_articles')}
              </button>
            </div>

            {/* Report Article link */}
            <button
              onClick={() => {
                if (!currentUserId) {
                  setModal({
                    visible: true,
                    message: t('articles:must_login_to_report'),
                    onOk: () => setModal({ visible: false, message: '', onOk: null })
                  });
                  return;
                }
                setShowReportModal(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#EF4444',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '0',
                opacity: 0.7,
                marginBottom: '16px'
              }}
            >
              <Flag size={12} />
              {t('articles:report_this_article')}
            </button>
          </div>
        </div>

        {/* Expandable Comments Panel */}
        {commentsExpanded ? (
          // Expanded state - slides up to 50vh
          <div style={{
            position: 'fixed',
            bottom: '60px',
            left: 0,
            right: 0,
            height: '50vh',
            backgroundColor: 'white',
            borderTop: '1px solid #E5E7EB',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100
          }}>
            {/* Header with collapse */}
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>
                {t('comments')} ({allComments.filter(c => !c.is_deleted_by_author).length})
              </span>
              <button
                onClick={() => setCommentsExpanded(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <ChevronDown size={24} color="#6B7280" />
              </button>
            </div>

            {/* Comments — shared ConcertinaThread engine (owns list + composer + interaction) */}
            <div style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '12px 16px'
            }}>
              <ConcertinaThread
                variant="comment"
                data={topLevelComments}
                loadResponses={loadResponses}
                onCreateTopLevel={onCreateTopLevel}
                onCreateReply={onCreateReply}
                onRefresh={reloadComments}
                reportContentType="chat"
                emptyState={t('no_comments_yet')}
              />
            </div>
          </div>
        ) : (
          // Collapsed state - tap to expand bar with first comment preview
          <div
            onClick={() => setCommentsExpanded(true)}
            style={{
              position: 'fixed',
              bottom: '60px',
              left: 0,
              right: 0,
              backgroundColor: '#F8FAFC',
              borderTop: '1px solid #E5E7EB',
              cursor: 'pointer',
              zIndex: 100,
              paddingTop: '8px',
              paddingBottom: '8px'
            }}
          >
            {/* Header row with count and chevron */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px 4px 16px'
            }}>
              <span style={{ fontWeight: '600', fontSize: '14px', color: '#1F2937' }}>
                {allComments.length} {t('comments')}
              </span>
              <ChevronUp size={20} color="#6B7280" />
            </div>

            {/* First comment preview - only first line */}
            {allComments.length > 0 && allComments.filter(c => !c.parent_chat_id)[0] && (
              <div style={{
                padding: '0 16px',
                fontSize: '13px',
                color: '#6B7280',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontStyle: 'italic'
              }}>
                {allComments.filter(c => !c.parent_chat_id)[0].content}
              </div>
            )}
          </div>
        )}

          {/* Mobile Footer */}
          <FooterContainer viewType="detail" isMobile={true}>
            <FooterButtonsDetail />
          </FooterContainer>
        </div>

        {/* Modals - Rendered outside mobile container to avoid z-index stacking issues */}
        <ConfirmationModal
          visible={modal.visible}
          title={t('notification')}
          message={modal.message}
          onOk={() => {
            try {
              modal.onOk && modal.onOk();
            } catch (e) {
              console.error(e);
              setModal({ visible: false, message: '', onOk: null });
            }
          }}
          onCancel={() => {
            setModal({ visible: false, message: '', onOk: null });
          }}
        />

        {/* Share Link Modal */}
        <ShareLinkModal
          visible={showShareModal}
          targetType="conversation"
          targetId={article.id}
          targetName={article.title}
          currentUser={userId ? { id: userId } : null}
          context={context}
          isPublic={article.is_public}
          communityId={article.community_id}
          onClose={() => setShowShareModal(false)}
        />

        {/* Report Article Modal */}
        <ReportModal
          visible={showReportModal}
          contentType="article"
          contentId={article.id}
          onClose={() => setShowReportModal(false)}
          onSubmit={async (reportData) => {
            const result = await userActionService.submitReport(currentUserId, reportData);
            if (result.success) {
              setModal({
                visible: true,
                message: t('common:report_submitted') || 'Report submitted. Thank you.',
                onOk: () => setModal({ visible: false, message: '', onOk: null })
              });
            }
          }}
        />

        {otherArticlesModal}

      </>
    );
  }

  // Shared article body — text/video/website branches. Rate/Share/Report live only in the
  // sidebar (always visible, at every desktop width) — no end-of-body duplicate needed now
  // that the sidebar is never replaced by a header block.
  const renderArticleBody = () => (
    isWebsiteArticle ? (
      /* WEBSITE ARTICLE: sandboxed embed + mandatory Open-site fallback */
      <div className="h-full overflow-y-auto p-6" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Open site — always visible; the mandatory fallback when the frame is blocked */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>{t('website_embed_note')}</p>
          <a
            href={article.website_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', padding: '8px 16px', background: '#024B99', color: '#FFFFFF', borderRadius: '8px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            {t('open_site')} {'↗'}
          </a>
        </div>
        {/* Sandboxed embed — contained, never trusted (no allow-top-navigation) */}
        <div style={{ position: 'relative', width: '100%', height: '70vh', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E5E7EB', marginBottom: '24px' }}>
          <iframe
            src={article.website_url}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            referrerPolicy="no-referrer"
            loading="lazy"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            title={article.title || 'Embedded website'}
          />
        </div>
        {/* Introduction */}
        {article.summary && (
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', lineHeight: '1.6', marginBottom: '16px' }}>
            {article.summary}
          </div>
        )}
        {/* Content */}
        <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
          {formatContent(article.content)}
          <div style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic', marginTop: '16px' }}>
            {t('end')}
          </div>
        </div>
      </div>
    ) : isVideoArticle ? (
      /* VIDEO ARTICLE: Vertical scrolling layout */
      <div className="h-full overflow-y-auto p-6" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* VIDEO EMBED - Responsive 16:9 wrapper */}
        {videoEmbedUrl ? (
          <div style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            marginBottom: '24px',
            borderRadius: '8px'
          }}>
            <iframe
              src={videoEmbedUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              title="Article Video"
            />
          </div>
        ) : (
          // Fallback: Show link if URL format not recognized
          <div style={{
            background: '#FFF3CD',
            border: '1px solid #FFEAA7',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '24px'
          }}>
            <p style={{ fontSize: '13px', color: '#856404', marginBottom: '8px' }}>
              <strong>{t('video_link')}:</strong>
            </p>
            <a
              href={article.video_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0066CC',
                fontSize: '12px',
                wordBreak: 'break-all'
              }}
            >
              {article.video_url}
            </a>
          </div>
        )}

        {/* Introduction - Bold and slightly larger */}
        {article.summary && (
          <div style={{
            fontSize: '15px',
            fontWeight: 'bold',
            color: '#1F2937',
            lineHeight: '1.6',
            marginBottom: '16px'
          }}>
            {article.summary}
          </div>
        )}

        {/* Content - Vertical scrolling */}
        <div style={{
          fontSize: '14px',
          color: '#374151',
          lineHeight: '1.6'
        }}>
          {formatContent(article.content)}

          <div style={{
            fontSize: '14px',
            color: '#6B7280',
            fontStyle: 'italic',
            marginTop: '16px'
          }}>
            {t('end')}
          </div>
        </div>
      </div>
    ) : (
      /* TEXT ARTICLE: Vertical scrolling layout with max-width constraint */
      <div
        ref={scrollRef}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100%',
          padding: '24px',
          scrollBehavior: 'smooth'
        }}
      >
        {/* Content container with max-width */}
        <div
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            fontSize: '14px',
            color: '#374151',
            lineHeight: '1.6'
          }}
        >
          {/* Introduction — bold */}
          {article.summary && (
            <p style={{
              fontSize: '15px',
              fontWeight: 'bold',
              color: '#1F2937',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>
              {article.summary}
            </p>
          )}

          {/* Article content */}
          {formatContent(article.content)}

          {/* End marker */}
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            fontStyle: 'italic',
            marginTop: '16px'
          }}>
            {t('end')}
          </p>
        </div>
      </div>
    )
  );

  // Desktop Layout
  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      style={{ backgroundColor: '#edf2f8' }}
    >
      {/* Header Bar - Layered architecture */}
      <HeaderContainer />
      <HeaderButtonsDetail
        currentContext={{ name: article.community_id ? article.group_name : (article.locality_name || t('local_community')) }}
        onBack={onBack}
      />

      {/* Main Layout - Sidebar + Article + Comments + News. Sidebar is the original, full-content
          design at every desktop width — the header-block reversal is complete: reversed, not a
          fallback tier. NewsGrid (the same component MainFeed/NewsFeed already uses, whole, not
          a bespoke card list) fills the slot to the right of Comments; its own auto-fit(250px)
          CSS grid decides 1 vs 2+ columns from whatever width remains — no separate breakpoint
          needed for that. */}
      {/* bottom: 0, not footerHeight — desktop article detail runs footerless (addendum-1,
          2026-09-05), following the front-page precedent (branding lives in the header instead;
          no dead band at the bottom). Mobile is unaffected — separate render branch, own
          FooterContainer untouched. */}
      <div className="flex" style={{ position: 'absolute', top: `${headerHeight}px`, bottom: 0, left: 0, right: 0 }}>

          {/* Left Sidebar - Dynamic width (384-480px based on viewport) */}
          <aside
            className="border-r border-gray-200 overflow-y-auto"
            style={{
              width: `${sidebarWidth}px`,
              minWidth: `${sidebarWidth}px`,
              padding: isSidebarCompact ? '12px 16px' : '12px 20px',
              transition: 'width 0.3s ease, padding 0.3s ease'
            }}
          >

            {/* 1. Top band - compact group/locality identity block (left) + hero image (right),
                two columns. Proportions are computed from the actual available content width
                (sidebarWidth minus its own padding) rather than fixed px, and the group image
                is sized directly off the hero's own computed width (not its column's), so it
                reliably comes out at half the hero's height at every sidebar width from the
                384px compressed floor to the 480px full width - see
                docs/briefs/2026-09-05-article-detail-left-sidebar-rearrange-and-responsive-footer.md
                and addendum-1 (2026-09-05, corrected left/right orientation + dropped the image
                title line, keeping only the credit). */}
            {(() => {
              const bandContentWidth = sidebarWidth - (isSidebarCompact ? 32 : 40);
              const BAND_GAP = 12;
              const heroColWidth = Math.round((bandContentWidth - BAND_GAP) * 0.62);
              const identityColWidth = bandContentWidth - BAND_GAP - heroColWidth;
              const heroHeight = Math.round(heroColWidth * 0.75); // 4:3
              const groupImageWidth = Math.round(heroColWidth * 0.5); // half the hero's linear size...
              const groupImageHeight = Math.round(groupImageWidth * 0.75); // ...so ~half its height too (same 4:3 ratio)
              const showHero = !isVideoArticle && (article.hero_image_display_url || article.cover_image_url);

              return (
                <div style={{
                  display: 'flex',
                  gap: `${BAND_GAP}px`,
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  {/* Left column: compact identity block - group info (group articles) or
                      locality attribution (locality-direct articles). Relocated from further
                      down the stack, not duplicated. */}
                  <div style={{ width: showHero ? `${identityColWidth}px` : '100%', minWidth: 0 }}>
                    {article.community_id ? (
                      <>
                        {article.group_logo_url && (
                          <img
                            src={article.group_logo_url}
                            alt={article.group_name || ''}
                            loading="lazy"
                            style={{
                              width: `${groupImageWidth}px`,
                              height: `${groupImageHeight}px`,
                              objectFit: 'cover',
                              borderRadius: '8px',
                              display: 'block'
                            }}
                          />
                        )}
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#1F2937',
                          marginTop: '6px',
                          marginBottom: '8px'
                        }}>
                          {article.group_name}
                        </div>
                        <StandardButton
                          variant="context"
                          label={t('view_group')}
                          width={identityColWidth}
                          wrapLabel
                          onClick={() => onViewGroup && onViewGroup(article.community_id, article.group_name)}
                        />
                      </>
                    ) : (
                      /* Locality-direct article - location attribution, no group button */
                      <>
                        <div style={{
                          width: `${groupImageHeight}px`,
                          height: `${groupImageHeight}px`,
                          borderRadius: '50%',
                          backgroundColor: '#EDF2F8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: `${Math.round(groupImageHeight * 0.45)}px`
                        }}>
                          📍
                        </div>
                        <div style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#6B7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginTop: '6px',
                          marginBottom: '2px'
                        }}>
                          {t('local_community')}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#1F2937'
                        }}>
                          {article.locality_name || t('local_community')}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Right column: hero image, credit directly underneath (image title/caption
                      dropped - addendum-1 - only the credit/attribution belongs here) */}
                  {showHero && (
                    <div style={{ width: `${heroColWidth}px`, flexShrink: 0 }}>
                      <img
                        src={article.hero_image_display_url || article.cover_image_url}
                        alt={article.title}
                        style={{
                          width: '100%',
                          height: `${heroHeight}px`,
                          objectFit: 'cover',
                          borderRadius: '8px',
                          display: 'block',
                          transition: 'width 0.3s ease, height 0.3s ease'
                        }}
                      />
                      {article.hero_image_credit ? (
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                          {article.hero_image_credit}
                        </div>
                      ) : article.hero_image_display_url ? (
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                          {t('image:credit.uncredited')}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 2. Article Title */}
            <div style={{ marginBottom: '12px' }}>
              <h1 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1F2937',
                lineHeight: '1.3'
              }}>
                {article.title}
              </h1>
            </div>

            {/* 3. Author + Stats (two-column layout) */}
            <div style={{ marginBottom: '12px', display: 'flex', gap: '16px' }}>
              {/* Left: Author */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '10px',
                  color: '#6B7280',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '2px'
                }}>
                  {t('article_author')}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '2px'
                }}>
                  {article.author_name || t('unknown_author')}
                </div>
              </div>

              {/* Right: Article Stats */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>{t('views')}:</span>
                    <span style={{ fontWeight: '600' }}>{viewCount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>{t('comments')}:</span>
                    <span style={{ fontWeight: '600' }}>{commentCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Rate this article (with interactive stars) + Report this article, grouped
                side by side - Report moved up from below the button row to sit beside the
                rating, above the buttons (addendum-2, 2026-09-05). */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
              {/* Left: Rate This Article with stars */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '6px'
                }}>
                  {t('rate_this_article')}
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map((starNumber) => {
                    const isFilled = userRating !== null && userRating >= starNumber;
                    return (
                      <Star
                        key={starNumber}
                        size={20}
                        fill={isFilled ? theme.primary : 'none'}
                        stroke={theme.primary}
                        strokeWidth={2}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleStarClick(starNumber)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Right: Report this article - same handler/modal as before, position only */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    if (!currentUserId) {
                      setModal({
                        visible: true,
                        message: t('articles:must_login_to_report'),
                        onOk: () => setModal({ visible: false, message: '', onOk: null })
                      });
                      return;
                    }
                    setShowReportModal(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#EF4444',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '0',
                    opacity: 0.7
                  }}
                >
                  <Flag size={12} />
                  {t('articles:report_this_article')}
                </button>
              </div>
            </div>

            {/* 5. Bottom section - Buttons row (group/locality info moved up into the top band;
                Report moved up beside the rating, above this row - addendum-2) */}
            <div style={{ marginTop: '20px' }}>
              {/* Top row: Share and See their other articles buttons side by side */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                {/* Share button */}
                <button
                  onClick={() => {
                    console.log('Desktop Share button clicked');
                    setShowShareModal(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: theme.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Share2 size={16} />
                  {t('share')}
                </button>

                {/* See author's other articles button */}
                <button
                  onClick={handleSeeOtherArticles}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'white',
                    color: theme.primary,
                    border: `2px solid ${theme.primary}`,
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  {t('see_their_other_articles')}
                </button>
              </div>
            </div>
          </aside>

          {/* Article Content Area - Vertical scrolling for both text and video articles.
              Hugs its own content's max-width (flexGrow:0 + maxWidth capped) instead of
              flex-growing to fill whatever's left before Comments. */}
          <main
            ref={contentAreaRef}
            className="overflow-hidden relative"
            style={{ flexGrow: 0, flexShrink: 1, flexBasis: 'auto', minWidth: 0, maxWidth: `${articleContainerMaxWidth}px` }}
          >
            {renderArticleBody()}
          </main>

          {/* Right Sidebar - Comments Section (300px). Header is pinned/non-scrolling and the
              body scrolls independently beneath it — same structure as ModeColumn's shell
              (LAYOUT.md: column header is non-scrolling). Previously the whole aside scrolled
              as one region with the heading inside it, which both let "Comments" scroll out of
              view under the fixed page header and produced a scrollbar with a single comment:
              ConcertinaThread's root sets height:'100%', which — inside a non-flex parent where
              the heading already occupies space above it — made the total content taller than
              the aside by roughly the heading's own height, regardless of comment count. */}
          <aside className="w-[300px] border-l border-gray-200" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <h3 style={{ flexShrink: 0, fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0, padding: '12px 12px 0', textAlign: 'center' }}>
              {t('comments')}
            </h3>
            {/* Comments — shared ConcertinaThread engine (owns list + composer + interaction).
                This wrapper only sizes the slot (flex:1/minHeight:0); ConcertinaThread's own
                internal body owns the actual scrolling, so there is exactly one scroll region. */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '12px' }}>
              <ConcertinaThread
                variant="comment"
                data={topLevelComments}
                loadResponses={loadResponses}
                onCreateTopLevel={onCreateTopLevel}
                onCreateReply={onCreateReply}
                onRefresh={reloadComments}
                reportContentType="chat"
                emptyState={t('no_comments_yet')}
              />
            </div>
          </aside>

          {/* News — NewsGrid reused whole (not a bespoke card list), same component MainFeed's
              NewsFeed already mounts. Its own auto-fit(250px) grid + overflowY:auto scroll
              decide column count and scrolling from whatever width this flex:1 slot resolves
              to — no separate 1/2-column breakpoint. Gated on this feature's 1280px product
              floor, and hidden (not empty-rendered) when there's no article list. */}
          {windowWidth >= 1280 && newsColumnArticles.length > 0 && (
            <div
              className="border-l border-gray-200"
              style={{ flex: 1, minWidth: '280px', overflow: 'hidden' }}
            >
              <NewsGrid
                articles={newsColumnArticles}
                onArticleClick={onArticleClick}
                isMobile={false}
                selectedGroup={null}
              />
            </div>
          )}
        </div>

      {/* No FooterContainer on desktop article detail (addendum-1, 2026-09-05) - the header
          already carries the OurNet logo, so nothing takes the footer's place. The universal
          Help "?" that FooterContainer used to carry is rehomed as its own floating button,
          matching the front-page precedent (MainFeed.jsx) for footerless pages. */}
      <div style={{ position: 'fixed', bottom: '40px', right: '40px', zIndex: 1100 }}>
        <StandardButton
          variant="icon"
          text="?"
          color="#F9A61B"
          title={t('ui:please_help')}
          onClick={() => setShowErrorReport(true)}
        />
      </div>

      <ErrorReportModal
        isOpen={showErrorReport}
        onClose={() => setShowErrorReport(false)}
        currentView="Article Detail"
        currentContext={{ name: 'Article Detail', type: 'detail', component: 'ArticleDetail' }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={modal.visible}
        title={t('notification')}
        message={modal.message}
        onOk={() => {
          try {
            modal.onOk && modal.onOk();
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
        onCancel={() => {
          setModal({ visible: false, message: '', onOk: null });
        }}
      />

      {/* Share Link Modal */}
      <ShareLinkModal
        visible={showShareModal}
        targetType="conversation"
        targetId={article.id}
        targetName={article.title}
        currentUser={userId ? { id: userId } : null}
        context={context}
        isPublic={article.is_public}
        communityId={article.community_id}
        onClose={() => setShowShareModal(false)}
      />

      {/* Report Article Modal */}
      <ReportModal
        visible={showReportModal}
        contentType="article"
        contentId={article.id}
        onClose={() => setShowReportModal(false)}
        onSubmit={async (reportData) => {
          const result = await userActionService.submitReport(currentUserId, reportData);
          if (result.success) {
            setModal({
              visible: true,
              message: t('common:report_submitted') || 'Report submitted. Thank you.',
              onOk: () => setModal({ visible: false, message: '', onOk: null })
            });
          }
        }}
      />

      {otherArticlesModal}

    </div>
  );
};

export default ArticleDetail;
